import React, { useReducer, useEffect } from 'react';

// Components and Others
import clsx from 'clsx';
import PropTypes from 'prop-types';
import DateUtils from 'lib/DateUtils';
import update from 'immutability-helper';
import DatePicker from 'react-datepicker';
import { Form, Col, Row } from 'react-bootstrap';
import { Language } from 'utils/enum/Customer';
import InputSSN from 'components/widgets/form/InputSSN';
import PhoneInput from 'components/widgets/form/PhoneInput';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';
import { isValidField, isValidSchema } from 'utils/schema/utils';
import { CustomerSchema, BusinessSchema } from 'utils/schema/customer/Customer';
import DialogActions from 'components/widgets/modal/DialogActions';
import Select from 'components/widgets/Select';
import CompanyDomainEmailInput from 'components/widgets/customer/CompanyDomainEmailInput';

// Material UI
import { makeStyles } from '@material-ui/core/styles';
import {
    DialogContent, Dialog, Grid,
    Tooltip,
} from '@material-ui/core';
import PhoneUtils from 'lib/PhoneUtils';
import CustomerMutate from 'services/graphQL/mutate/CustomerMutate';
import { useMutation, useApolloClient } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import CustomerQuery from 'services/graphQL/query/CustomerQuery';
import SuggestCustomer from 'components/widgets/customer/SuggestCustomer';
import ModalUtils from 'utils/ModalUtils';
import StringUtils from 'lib/StringUtils';
import { MEDIA_QUERY } from 'utils/enum/Core';

const useStyles = makeStyles((theme) => ({
    dialogContent: {
        padding: theme.spacing(3, 1.5),
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            '& .am-form': {
                flexDirection: 'column',
                flexWrap: 'nowrap',
                overflow: 'auto',
            },
            '& .am-form .MuiGrid-item': {
                marginTop: '0px',
            },
        },
    },
    businessCheckBox: {
        marginBottom: theme.spacing(2),
        marginLeft: theme.spacing(2),
    },
    checkLabel: {
        fontSize: '13px !important',
        fontWeight: '500',
    },
    secondColumnMargin: {
        marginTop: '34px',
        '& > div:nth-child(5)': {
            marginTop: '15px',
        },
    },
    topRow: {
        marginLeft: 0,
        marginRight: 0,
        paddingLeft: '15px',
        '& > div:nth-child(2)': {
            marginLeft: '10px',
        },
    },
    row: {
        '& > div:nth-child(2)': {
            marginBottom: 0,
        },
    },
}));

const initState = {
};

const ACTION_TYPES = {
    ON_CHANGE_VALUE: 'onChangeValue',
    SET_INITIAL_STATE: 'setInitialState',
};

const reducer = (state, action) => {
    switch (action.type) {
    case ACTION_TYPES.ON_CHANGE_VALUE: {
        return update(state, {
            [action.field]: { $set: action.payload },
        });
    }
    case ACTION_TYPES.SET_INITIAL_STATE: {
        const record = action.payload;
        return update(state, {
            firstName: { $set: record.firstName },
            lastName: { $set: record.lastName },
            ssn: { $set: record.ssn },
            email: { $set: record.email },
            workPhone: { $set: PhoneUtils.format(record.workPhone) },
            middleName: { $set: record.middleName },
            dob: { $set: record.dob },
            dln: { $set: record.dln },
            cellPhone: { $set: PhoneUtils.format(record.cellPhone) },
            homePhone: { $set: PhoneUtils.format(record.homePhone) },
            isBusiness: { $set: record.isBusiness },
            allowTextCellPhone: { $set: record.allowTextCellPhone },
            allowCallCellPhone: { $set: record.allowCallCellPhone },
            allowCallHomePhone: { $set: record.allowCallHomePhone },
            allowCallWorkPhone: { $set: record.allowCallWorkPhone },
            isTaxable: { $set: record.isTaxable },
            language: { $set: record.language },
            inHouseServiceCustomer: { $set: record.inHouseServiceCustomer },
            openModalSuggestedCustomer: { $set: false },
            suggestedCustomer: { $set: null },
        });
    }
    case 'TOGGLE_SUGGEST_CUSTOMER': {
        return update(state, {
            openModalSuggestedCustomer: { $set: action.payload },
        });
    }
    case 'SET_SUGGESTED_CUSTOMER': {
        return update(state, {
            suggestedCustomer: { $set: action.payload },
            openModalSuggestedCustomer: { $set: true },
        });
    }
    default:
        return state;
    }
};

const EditCustomerDialog = ({
    open, record, toggleModal, isCustomerModule, onUseSuggested,
}) => {
    const classes = useStyles();
    const client = useApolloClient();
    const history = useHistory();
    const [loadingSuggestedCustomer, setLoadingSuggestedCustomer] = React.useState(false);
    const [state, dispatch] = useReducer(reducer, initState);
    const [updateCustomer] = useMutation(CustomerMutate.UPDATE_CUSTOMER);
    const schema = state.isBusiness ? BusinessSchema : CustomerSchema;
    const isValidData = isValidSchema(schema, Object.assign(state, { dob: new Date(state.dob) }));
    const { isValid, errors } = isValidData;

    const onChangeValue = (field, value) => {
        dispatch({
            type: ACTION_TYPES.ON_CHANGE_VALUE,
            field,
            payload: value,
        });
    };

    const toggleCustomerSuggest = (isOpen = false) => {
        dispatch({ type: 'TOGGLE_SUGGEST_CUSTOMER', payload: isOpen });
    };

    const onSave = async () => {
        try {
            const input = {
                customerId: record.customerId,
                input: {
                    firstName: state.firstName,
                    lastName: state.lastName,
                    ssn: state.ssn,
                    email: !StringUtils.isEmpty(state.email) ? state.email.trim() : null,
                    workPhone: state.workPhone,
                    middleName: state.middleName,
                    dob: state.dob ? DateUtils.getOnlyDate(state.dob) : null,
                    dln: state.dln,
                    cellPhone: state.cellPhone,
                    homePhone: state.homePhone,
                    isBusiness: state.isBusiness,
                    allowTextCellPhone: state.allowTextCellPhone,
                    allowCallCellPhone: state.allowCallCellPhone,
                    allowCallHomePhone: state.allowCallHomePhone,
                    allowCallWorkPhone: state.allowCallWorkPhone,
                    isTaxable: state.isTaxable,
                    language: state.language,
                    inHouseServiceCustomer: state.inHouseServiceCustomer,
                },
            };

            const response = await updateCustomer({ variables: input });

            if (response.data?.updateCustomer) {
                toggleModal();
            }
        } catch (ex) {
            ModalUtils.errorMessage(ex?.graphQLErrors || [], ex?.message || null);
        }
    };

    const handleSaveValidation = async () => {
        const {
            firstName, lastName, cellPhone, homePhone, email,
        } = state;
        const phone = cellPhone || homePhone;

        if (!firstName || (!lastName && !email && !phone)) {
            onSave();
            return;
        }

        try {
            setLoadingSuggestedCustomer(true);
            const { data, errors: graphqlErrors } = await client.query({
                query: CustomerQuery.GET_CUSTOMER_DUPLICATE_SEARCH,
                variables: {
                    firstName: firstName || null,
                    lastName: lastName || null,
                    phone: phone || null,
                    email: email || null,
                    customerId: record.customerId,
                },
                fetchPolicy: 'network-only',
                errorPolicy: 'all',
            });
            setLoadingSuggestedCustomer(false);

            if (graphqlErrors && graphqlErrors.length > 0) {
                ModalUtils.errorMessage(graphqlErrors);
                return;
            }

            if (data?.getCustomerDuplicateSearch && data.getCustomerDuplicateSearch.length > 0) {
                dispatch({
                    type: 'SET_SUGGESTED_CUSTOMER',
                    payload: data.getCustomerDuplicateSearch,
                });
            } else {
                onSave();
            }
        } catch (ex) {
            setLoadingSuggestedCustomer(false);
            ModalUtils.errorMessage(ex?.graphQLErrors || [], ex?.message || null);
        }
    };

    const onUseEnteredCustomer = () => {
        toggleCustomerSuggest(false);
        onSave();
    };

    const onUseSuggestedCustomer = async (customer) => {
        toggleCustomerSuggest(false);
        // User is replacing the current customer with the suggested customer

        if (isCustomerModule) {
            toggleModal();
            history.push(`/customers/${customer.customerCode}`);
            return;
        }

        if (onUseSuggested) {
            try {
                setLoadingSuggestedCustomer(true);
                const { data, errors: graphqlErrors } = await client.query({
                    query: CustomerQuery.GET_CUSTOMER_AND_ADDRESS,
                    variables: { customerCode: customer.customerCode },
                    fetchPolicy: 'network-only',
                    errorPolicy: 'all',
                });
                setLoadingSuggestedCustomer(false);

                if (graphqlErrors && graphqlErrors.length > 0) {
                    ModalUtils.errorMessage(graphqlErrors);
                    return;
                }

                const fullCustomer = data?.getCustomerAndAddress;
                if (!fullCustomer) {
                    ModalUtils.errorMessage([], 'Unable to load suggested customer details.');
                    return;
                }

                onUseSuggested(fullCustomer);
                toggleModal();
            } catch (ex) {
                setLoadingSuggestedCustomer(false);
                ModalUtils.errorMessage(ex?.graphQLErrors || [], ex?.message || null);
            }
            return;
        }

        // Fallback behavior
        toggleModal();
        history.push(`/customers/${customer.customerCode}`);
    };

    useEffect(() => {
        dispatch({
            type: ACTION_TYPES.SET_INITIAL_STATE,
            payload: record,
        });
    }, [record]);

    const internalCustomerTooltipMessage = 'This customer is marked as Internal and cannot be edited.';
    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="md"
            PaperProps={{
                className: clsx(classes.dialog, state.isBusiness ? classes.businessDialogHeigh : classes.dialogHeight),
            }}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogAppBar title="Edit" onClose={toggleModal} iconSize="sm" />
            <DialogContent className={classes.dialogContent}>
                <Grid container className="am-form">
                    <Grid item xs={6}>
                        <Form.Group className={classes.topRow} as={Row}>
                            <Form.Check type="checkbox" id="businessDeals">
                                <Form.Check.Input
                                    type="checkbox"
                                    checked={state.isBusiness}
                                    onChange={(e) => onChangeValue('isBusiness', e.target.checked)}
                                />
                                <Form.Check.Label className={classes.checkLabel}>Business</Form.Check.Label>
                            </Form.Check>
                            <Form.Check type="checkbox" id="isTaxableId" className="mt-8">
                                <Form.Check.Input
                                    type="checkbox"
                                    checked={state.isTaxable}
                                    onChange={(e) => onChangeValue('isTaxable', e.target.checked)}
                                />
                                <Form.Check.Label className={classes.checkLabel}>Is taxable</Form.Check.Label>
                            </Form.Check>
                        </Form.Group>
                        <Form.Group as={Col}>
                            <Form.Label className="required">{state.isBusiness ? 'Business Name' : 'First Name'}</Form.Label>
                            <Tooltip
                                title={state.inHouseServiceCustomer ? internalCustomerTooltipMessage : ''}
                                placement="top"
                            >
                                <Form.Control
                                    type="text"
                                    size="sm"
                                    placeholder="First Name"
                                    value={state.firstName}
                                    maxLength={70}
                                    onChange={(e) => onChangeValue('firstName', e.target.value)}
                                    className={isValidField(errors, 'firstName') ? 'invalid-field' : ''}
                                    disabled={state.inHouseServiceCustomer}
                                />
                            </Tooltip>
                        </Form.Group>
                        {!state.isBusiness && (
                            <Form.Group as={Col}>
                                <Form.Label>Middle Name</Form.Label>
                                <Tooltip
                                    title={state.inHouseServiceCustomer ? internalCustomerTooltipMessage : ''}
                                    placement="top"
                                >
                                    <Form.Control
                                        type="text"
                                        size="sm"
                                        placeholder="Middle Name"
                                        value={state.middleName || ''}
                                        maxLength={70}
                                        onChange={(e) => onChangeValue('middleName', e.target.value)}
                                        disabled={state.inHouseServiceCustomer}
                                    />
                                </Tooltip>
                            </Form.Group>
                        )}
                        {!state.isBusiness && (
                            <Form.Group as={Col}>
                                <Form.Label className="required">Last Name</Form.Label>
                                <Tooltip
                                    title={state.inHouseServiceCustomer ? internalCustomerTooltipMessage : ''}
                                    placement="top"
                                >
                                    <Form.Control
                                        type="text"
                                        size="sm"
                                        placeholder="Last Name"
                                        value={state.lastName || ''}
                                        maxLength={70}
                                        onChange={(e) => onChangeValue('lastName', e.target.value)}
                                        className={!state.inHouseServiceCustomer && isValidField(errors, 'lastName') ? 'invalid-field' : ''}
                                        disabled={state.inHouseServiceCustomer}
                                    />
                                </Tooltip>
                            </Form.Group>
                        )}
                        <Form.Group as={Col}>
                            <Form.Label>SSN</Form.Label>
                            <InputSSN
                                size="sm"
                                value={state.ssn}
                                onChange={(value) => onChangeValue('ssn', value)}
                            />
                        </Form.Group>
                        <Form.Group as={Col}>
                            <Form.Label>Email</Form.Label>
                            <CompanyDomainEmailInput
                                placeholder="email@address.com"
                                value={state.email}
                                maxLength={100}
                                onChange={(e) => onChangeValue('email', e.target.value)}
                                className={isValidField(errors, 'email') ? 'invalid-field' : ''}
                            />
                        </Form.Group>
                        <Form.Group as={Col}>
                            <Form.Label>Language</Form.Label>
                            <Select
                                nowrap
                                size="sm"
                                name="language"
                                loading={false}
                                className={classes.input}
                                onChange={(name, value) => onChangeValue(name, value)}
                                value={state.language || ''}
                                options={Object.values(Language).map((l) => ({
                                    value: l,
                                    label: l,
                                }))}
                            />
                        </Form.Group>
                    </Grid>
                    <Grid item xs={6} className={classes.secondColumnMargin}>
                        <Form.Group as={Col} className="full-width-date-picker">
                            <Form.Label className={clsx(classes.datePicker, 'required')}>DOB</Form.Label>
                            <DatePicker
                                portalId="dob"
                                selected={new Date(DateUtils.getOnlyDate(state.dob))}
                                placeholderText="mm/dd/yyyy"
                                maxDate={new Date()}
                                onChange={(date) => onChangeValue('dob', date)}
                                className={clsx('form-control', 'form-control-sm', isValidField(errors, 'dob') ? 'invalid-field' : '')}
                            />
                        </Form.Group>
                        <Form.Group as={Col}>
                            <Form.Label>DLN</Form.Label>
                            <Form.Control
                                type="text"
                                size="sm"
                                placeholder="DLN"
                                value={state.dln || ''}
                                maxLength={20}
                                onChange={(e) => onChangeValue('dln', e.target.value)}
                            />
                        </Form.Group>
                        <Row className="mb-3 col">
                            <Form.Group as={Col}>
                                <Form.Label>Cell Phone</Form.Label>
                                <PhoneInput
                                    size="sm"
                                    value={state.cellPhone}
                                    onChange={(value) => onChangeValue('cellPhone', value)}
                                    className={isValidField(errors, 'cellPhone') ? 'invalid-field' : ''}
                                />
                            </Form.Group>
                            <div className="form-group d-flex-direction-column-justify-flex-end">
                                <Form.Check type="checkbox" id="allow-messages">
                                    <Form.Check.Input
                                        type="checkbox"
                                        checked={state.allowTextCellPhone}
                                        onChange={(e) => onChangeValue('allowTextCellPhone', e.target.checked)}
                                    />
                                    <Form.Check.Label className={classes.checkLabel}>Allow messages</Form.Check.Label>
                                </Form.Check>

                                <Form.Check type="checkbox" id="allow-calls" className="mt-8">
                                    <Form.Check.Input
                                        type="checkbox"
                                        checked={state.allowCallCellPhone}
                                        onChange={(e) => onChangeValue('allowCallCellPhone', e.target.checked)}
                                    />
                                    <Form.Check.Label className={classes.checkLabel}>Allow calls</Form.Check.Label>
                                </Form.Check>
                            </div>
                        </Row>
                        <Row className={clsx('mb-3 col', classes.row)}>
                            <Form.Group as={Col} className="mb-0">
                                <Form.Label>Work Phone</Form.Label>
                                <PhoneInput
                                    size="sm"
                                    value={state.workPhone}
                                    onChange={(value) => onChangeValue('workPhone', value)}
                                />
                            </Form.Group>
                            <div className="form-group d-flex-direction-column-justify-flex-end">
                                <Form.Label />
                                <Form.Check type="checkbox" id="allow-calls-work-phone" className="mt-8">
                                    <Form.Check.Input
                                        type="checkbox"
                                        checked={state.allowCallWorkPhone}
                                        onChange={(e) => onChangeValue('allowCallWorkPhone', e.target.checked)}
                                    />
                                    <Form.Check.Label className={classes.checkLabel}>Allow calls</Form.Check.Label>
                                </Form.Check>
                            </div>
                        </Row>
                        <Row className={clsx('mb-3 col', classes.row)}>
                            <Form.Group as={Col} className="mb-0">
                                <Form.Label>Home Phone</Form.Label>
                                <PhoneInput
                                    size="sm"
                                    value={state.homePhone}
                                    onChange={(value) => onChangeValue('homePhone', value)}
                                />
                            </Form.Group>
                            <div className="form-group d-flex-direction-column-justify-flex-end">
                                <Form.Label />
                                <Form.Check type="checkbox" id="allow-calls-home-phone" className="mt-8">
                                    <Form.Check.Input
                                        type="checkbox"
                                        checked={state.allowCallHomePhone}
                                        onChange={(e) => onChangeValue('allowCallHomePhone', e.target.checked)}
                                    />
                                    <Form.Check.Label className={classes.checkLabel}>Allow calls</Form.Check.Label>
                                </Form.Check>
                            </div>
                        </Row>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions
                onClickSecondary={toggleModal}
                onClickPrimary={handleSaveValidation}
                disablePrimaryButton={!isValid || loadingSuggestedCustomer}
            />
            {state.openModalSuggestedCustomer && (
                <SuggestCustomer
                    open={state.openModalSuggestedCustomer}
                    onClose={() => toggleCustomerSuggest(false)}
                    suggestedCustomer={state.suggestedCustomer}
                    enteredCustomer={{
                        firstName: state.firstName,
                        lastName: state.lastName,
                        email: state.email,
                        cellPhone: state.cellPhone,
                        address1: state.address1,
                    }}
                    onUseEntered={onUseEnteredCustomer}
                    onUseSuggested={onUseSuggestedCustomer}
                    isCustomerModule={isCustomerModule}
                />
            )}
        </Dialog>
    );
};

EditCustomerDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    record: PropTypes.object.isRequired,
    toggleModal: PropTypes.func.isRequired,
    isCustomerModule: PropTypes.bool,
    onUseSuggested: PropTypes.func,
};

EditCustomerDialog.defaultProps = {
    isCustomerModule: false,
    onUseSuggested: null,
};

export default EditCustomerDialog;
